class ShopifyDiscountManager {
  constructor(config) {
    this.config = {
      // CHANGED: This is now just a list of valid codes, no percentages.
      validDiscountCodes: [],
      // NEW: Configuration for the discount tag.
      discountTagPrefix: 'discount',
      dataAttributeHostSelector: '[data-product-id]', // Selector for the element holding the data-discount-tag

      priceSelectors: {
        priceContainer: '.price',
        currentPrice: 'span.theme-money:not(.was-price)',
        wasPrice: 'span.was-price',
        productPage: '.price-area.product-detail__gap-sm'
      },
      cookieName: 'discount_code',
      styles: `
        .price-updating {
          opacity: 0;
          transition: opacity 0.1s;
        }
        .price-updated {
          opacity: 1;
        }
      `,
      currencyFormats: {
        USD: {
          decimals: 2,
          decimalSeparator: '.',
          thousandsSeparator: ',',
          symbol: '$'
        },
        EUR: {
          decimals: 2,
          decimalSeparator: ',',
          thousandsSeparator: '.',
          symbol: '€'
        },
        PLN: {
          decimals: 2,
          decimalSeparator: ',',
          thousandsSeparator: ' ',
          symbol: 'zł'
        },
        // NEW: Added GBP Support
        GBP: {
          decimals: 2,
          decimalSeparator: '.',
          thousandsSeparator: ',',
          symbol: '£'
        }
      },
      giftCards: {
        productIds: [],
        variantIds: []
      },
      ...config
    };
    
    this.originalPrices = new Map();
    this.observer = null;
    this.initialized = false;
    this.styleElement = null;
    
    this.updateAllPrices = this.updateAllPrices.bind(this);
    this.handleMutations = this.handleMutations.bind(this);
  }

  init() {
    if (this.initialized) {
      console.warn('Discount manager already initialized');
      return;
    }
    
    const discountCode = this.getDiscountCookie();
    if (!discountCode || !this.isValidDiscountCode(discountCode)) {
      console.debug('No valid discount code found, manager will not run.');
      return;
    }

    try {
      this.injectStyles();
      this.setupPriceObserver();
      this.updateAllPrices();
      this.initialized = true;
      console.debug('Discount manager initialized successfully');
    } catch (error) {
      console.error('Failed to initialize discount manager:', error);
    }
  }

  injectStyles() {
    if (!this.styleElement) {
      this.styleElement = document.createElement('style');
      this.styleElement.textContent = this.config.styles;
      document.head.appendChild(this.styleElement);
    }
  }

  // CHANGED: Now checks if the code is in the array.
  isValidDiscountCode(code) {
    return this.config.validDiscountCodes.includes(code);
  }

  getDiscountCookie() {
    try {
      return document.cookie
        .split('; ')
        .find(row => row.startsWith(`${this.config.cookieName}=`))
        ?.split('=')[1];
    } catch (error) {
      console.error('Error reading discount cookie:', error);
      return null;
    }
  }

  detectCurrencyFormat(priceString) {
    if (!priceString || typeof priceString !== 'string') return this.config.currencyFormats.USD;
    const cleaned = priceString.trim();
    
    // NEW: Check for GBP
    if (cleaned.includes('£') || cleaned.startsWith('GBP')) return this.config.currencyFormats.GBP;
    
    if (cleaned.includes('€') || cleaned.startsWith('EUR')) return this.config.currencyFormats.EUR;
    if (cleaned.includes('zł') || cleaned.startsWith('PLN')) return this.config.currencyFormats.PLN;
    return this.config.currencyFormats.USD;
  }

  parsePriceString(priceString) {
    if (!priceString || typeof priceString !== 'string') {
      console.warn('Invalid price string:', priceString);
      return 0;
    }
    try {
      const format = this.detectCurrencyFormat(priceString);
      let cleaned = priceString.replace(/[^\d.,]/g, '');
      if (format.decimalSeparator === ',') {
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        cleaned = cleaned.replace(/,/g, '');
      }
      return parseFloat(cleaned);
    } catch (error) {
      console.error('Error parsing price:', error);
      return 0;
    }
  }

  // CHANGED: This is the core logic change. It now calculates the discount based on the element's data attribute.
  calculateDiscount(price, priceContainerElement) {
    if (typeof price !== 'number' || isNaN(price) || !priceContainerElement) {
      return price; // Return original price if data is invalid
    }

    // Find the product container that holds the data-discount-tag
    const productHost = priceContainerElement.closest(this.config.dataAttributeHostSelector);
    if (!productHost) {
      return price; // No host found, no discount
    }

    const discountTag = productHost.dataset.discountTag;
    if (!discountTag) {
      return price; // No valid discount tag, no discount
    }
    
    // ============================================================
    // OPTION A: BLACK FRIDAY MODE (ACTIVE)
    // Logic: Boolean check. If tag is 'blackfriday', apply fixed 20%.
    // ============================================================
    if (discountTag === 'blackfriday') {
      const discountRate = 0.20; // 20%
      return price * (1 - discountRate);
    }

    // ============================================================
    // OPTION B: DYNAMIC TAG MODE (COMMENTED OUT - PRESERVED)
    // Logic: Reads 'discount20' and applies 20%.
    // ============================================================
    /*
    if (!discountTag.startsWith(this.config.discountTagPrefix)) {
      return price; 
    }
    
    // Extract the number from the tag (e.g., "sale15" -> "15")
    const numberPart = discountTag.replace(this.config.discountTagPrefix, '');
    const discountPercentage = parseInt(numberPart, 10);
    
    // Validate that we got a real number
    if (isNaN(discountPercentage) || discountPercentage <= 0 || discountPercentage >= 100) {
      console.warn('Invalid discount percentage found in tag:', discountTag);
      return price; // Invalid percentage, no discount
    }
    
    const discountRate = discountPercentage / 100;
    return price * (1 - discountRate);
    */
    
    // If we reach here, no discount logic matched
    return price;
  }

  formatPrice(price, template) {
    if (!template || typeof price !== 'number' || isNaN(price)) {
      console.warn('Invalid price or template for formatting:', { price, template });
      return template || '0.00';
    }
    try {
      const format = this.detectCurrencyFormat(template);
      let formattedPrice = price.toFixed(format.decimals);
      if (format.decimalSeparator === ',') {
        formattedPrice = formattedPrice.replace('.', ',');
      }
      const parts = formattedPrice.split(format.decimalSeparator);
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);
      formattedPrice = parts.join(format.decimalSeparator);
      const trimmedTemplate = template.trim();
      const symbolIsPrefix = /^[^\d]/.test(trimmedTemplate);
      if (symbolIsPrefix) {
        return `${trimmedTemplate.charAt(0)}${formattedPrice}`;
      } else {
        return `${formattedPrice} ${trimmedTemplate.slice(-2)}`;
      }
    } catch (error) {
      console.error('Error formatting price:', error);
      return template;
    }
  }

  generatePriceId() {
    return `price_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  isGiftCard(element) {
    if (!element) return false;
    try {
      const productContainer = element.closest('[data-product-id]');
      if (!productContainer) return false;
      const productId = productContainer.dataset.productId;
      const variantId = productContainer.querySelector('[data-variant-id]')?.dataset.variantId;
      const isGiftCard = (
        this.config.giftCards.productIds.includes(productId) ||
        (variantId && this.config.giftCards.variantIds.includes(variantId))
      );
      if (isGiftCard) console.debug('Gift card detected:', { productId, variantId });
      return isGiftCard;
    } catch (error) {
      console.error('Error checking for gift card:', error);
      return false;
    }
  }

  isProductPage(element) {
    return !!element.closest('.product-area');
  }

  updatePriceElement(priceContainer) {
    const discountCode = this.getDiscountCookie();
    if (!discountCode || !this.isValidDiscountCode(discountCode)) {
      return; 
    }
    if (!priceContainer || !(priceContainer instanceof Element)) {
      console.warn('Invalid price container:', priceContainer);
      return;
    }
    if (this.isGiftCard(priceContainer)) {
      console.debug('Skipping discount for gift card');
      return;
    }
    
    try {
      priceContainer.classList.add('price-updating');
      const priceId = priceContainer.dataset.priceId || this.generatePriceId();
      priceContainer.dataset.priceId = priceId;

      if (!this.originalPrices.has(priceId)) {
        const currentPriceEl = priceContainer.querySelector(this.config.priceSelectors.currentPrice);
        if (!currentPriceEl) return;
        this.originalPrices.set(priceId, {
          container: priceContainer,
          originalHTML: priceContainer.innerHTML,
          currentPrice: currentPriceEl.textContent,
        });
      }

      const original = this.originalPrices.get(priceId);
      const basePrice = this.parsePriceString(original.currentPrice);
      // CHANGED: Pass the element to calculateDiscount
      const discountedPrice = this.calculateDiscount(basePrice, priceContainer);
      
      // NEW: If no discount was applied, abort to avoid changing the HTML.
      if (discountedPrice >= basePrice) {
        console.debug('No applicable discount found for this item. Skipping update.');
        priceContainer.classList.remove('price-updating');
        return;
      }

      // --- LOGIC CHANGE FOR COMPARE AT PRICE ---
      // We check the ORIGINAL HTML to see if there was already a "Was Price" (item on sale).
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = original.originalHTML;
      const existingWasPriceElement = tempDiv.querySelector(this.config.priceSelectors.wasPrice);
      
      let wasPriceHTML;
      if (existingWasPriceElement) {
        // If item was already on sale ($100 -> $80), keep showing $100 as the crossed out price
        wasPriceHTML = existingWasPriceElement.outerHTML;
      } else {
        // If item was regular price, show the current selling price as the crossed out price
        wasPriceHTML = `<span class="was-price theme-money">${original.currentPrice}</span>`;
      }
      // -----------------------------------------

      const isProductPage = this.isProductPage(priceContainer);
      const fontSizeStyle = isProductPage ? ' style="font-size: 1.25em;"' : '';

      priceContainer.innerHTML = `
        <span class="theme-money"${fontSizeStyle}>${this.formatPrice(discountedPrice, original.currentPrice)}</span>
        ${wasPriceHTML}
      `;

      requestAnimationFrame(() => {
        priceContainer.classList.remove('price-updating');
        priceContainer.classList.add('price-updated');
      });
    } catch (error) {
      console.error('Error updating price element:', error);
      const original = this.originalPrices.get(priceContainer.dataset.priceId);
      if (original) priceContainer.innerHTML = original.originalHTML;
      priceContainer.classList.remove('price-updating');
    }
  }

  updateAllPrices() {
    const containers = document.querySelectorAll(this.config.priceSelectors.priceContainer);
    containers.forEach(container => this.updatePriceElement(container));
  }

  handleMutations(mutations) {
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.matches(this.config.priceSelectors.priceContainer) || node.querySelector(this.config.priceSelectors.priceContainer)) {
              this.updateAllPrices();
            }
          }
        });
      }
    });
  }

  setupPriceObserver() {
    if (this.observer) this.observer.disconnect();
    this.observer = new MutationObserver(this.handleMutations);
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  destroy() {
    try {
      if (this.observer) this.observer.disconnect();
      if (this.styleElement) {
        this.styleElement.remove();
        this.styleElement = null;
      }
      this.originalPrices.forEach(({container, originalHTML}) => {
        if (container && container instanceof Element) {
          container.innerHTML = originalHTML;
          container.classList.remove('price-updating', 'price-updated');
        }
      });
      this.originalPrices.clear();
      this.initialized = false;
      console.debug('Discount manager destroyed successfully');
    } catch (error) {
      console.error('Error destroying discount manager:', error);
    }
  }
}